<template>
    <v-app-bar v-click-outside="resetMenu" app :height="height" role="banner" extension-height="0" class="site-header overflow-visible">
        <template #default>
            <v-sheet color="header" :height="height" class="site-header__content px-4 px-md-10">
                <div class="d-flex align-center">
                    <div class="d-flex align-center site-logo__container">
                        <site-logo :href="menuItems.baseUrl" />
                    </div>

                    <site-top-menu
                        v-if="!isMobile"
                        v-model:selected-menu-item="desktopMenu"
                        :menu-items="topMenu"
                        :compact-mode="compactMenu"
                    />

                    <v-spacer />

                    <template v-if="isMobile">
                        <v-btn variant="text" class="menu-btn" @click="mobileMenu = !mobileMenu">
                            {{ t('$vuetify.menu.title') }}
                            <v-icon
                                :icon="mobileMenu ? 'kdi-close' : 'kdi-menu'"
                                size="22"
                                color="green"
                                class="menu-btn__icon ml-1"
                                :class="{ 'menu-btn__icon--active': mobileMenu }"
                            />
                        </v-btn>
                    </template>
                    <template v-else>
                        <my-profile-status
                            v-model:selected-menu-item="desktopMenu"
                            :menu-items="menuItems"
                            :is-logged-in="isLoggedIn"
                            :profile-info="profileInfo"
                            :current-language="currentLanguage"
                            :compact-mode="compactMenu"
                        />

                        <language-switcher
                            v-if="!hideLanguageSwitcher"
                            v-model="_currentLanguage"
                            :supported-languages="menuItems?.supportedLanguages"
                        />

                        <div v-if="menuItems.searchAutocompleteLink && searchPage" class="d-flex align-center flex-nowrap">
                            <v-slide-x-reverse-transition hide-on-leave>
                                <v-sheet v-if="expandSearch" :width="compactMenu ? 250 : 300" color="transparent">
                                    <search-bar
                                        v-click-outside="() => (expandSearch = false)"
                                        :autocomplete-link="menuItems.searchAutocompleteLink"
                                        :search-page="searchPage"
                                        bg-color="white"
                                        color="primary"
                                        autofocus
                                    />
                                </v-sheet>
                            </v-slide-x-reverse-transition>
                            <v-slide-x-transition hide-on-leave>
                                <v-btn v-show="!expandSearch" variant="text" @click="expandSearch = true">
                                    <template #append>
                                        <v-icon icon="mdi-magnify" size="20" color="white" />
                                    </template>
                                    {{ searchPage.displayName }}
                                </v-btn>
                            </v-slide-x-transition>
                        </div>

                        <!-- <v-btn variant="text" :href="contactUs?.link">
                            <template #prepend>
                                <v-sheet rounded="circle" width="20" height="20" class="d-flex align-center justify-center">
                                    <v-icon icon="mdi-phone" size="12" color="green" />
                                </v-sheet>
                            </template>
                            {{ contactUsPage?.displayName }}
                        </v-btn> -->
                    </template>
                </div>
            </v-sheet>
        </template>

        <template #extension>
            <site-top-mobile-menu
                v-if="isMobile"
                v-model="mobileMenu"
                v-model:current-language="_currentLanguage"
                :menu-items="menuItems"
                :is-logged-in="isLoggedIn"
                :profile-info="profileInfo"
            >
                <template #prepend>
                    <div v-if="menuItems.searchAutocompleteLink && searchPage" class="pa-4">
                        <search-bar
                            :autocomplete-link="menuItems.searchAutocompleteLink"
                            :search-page="searchPage"
                            :placeholder="searchPage.displayName"
                        />
                    </div>
                </template>
            </site-top-mobile-menu>
            <div v-else>
                <site-profile-menu-dropdown
                    :selected-menu-item="desktopMenu"
                    :profile-info="profileInfo"
                    :is-logged-in="isLoggedIn"
                    :menu-items="menuItems"
                    :current-language="currentLanguage"
                />
                <site-top-menu-dropdown :menu-items="topMenu" :selected-menu-item="desktopMenu" />
            </div>
        </template>
    </v-app-bar>
</template>

<script setup lang="ts">
import { VAppBar, VSpacer, VBtn, VIcon, VSheet, VSlideXTransition, VSlideXReverseTransition } from 'vuetify/components';
import { useLocale } from 'vuetify';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { MyProfileStatus } from '../MyProfileStatus';
import { ref } from 'vue';
import { SiteLogo } from '../SiteLogo';
import { SiteTopMenu, SiteTopMenuDropdown, SiteTopMobileMenu, useSiteTopMenu, SiteProfileMenuDropdown } from '../SiteTopMenu';
import { MenuStructure, useSiteNavigation } from '../../composables';
import { useSiteHeader } from '.';
import { useVModel } from '@vueuse/core';
import { SearchBar } from '../SearchBar';
import { ProfileInfo } from '../../models';

interface Props {
    menuItems: MenuStructure;
    currentLanguage: string;
    isLoggedIn: boolean;
    profileInfo?: ProfileInfo;
    hideLanguageSwitcher?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    menuItems: () => ({
        baseUrl: '/'
    })
});
const { t } = useLocale();
const { searchPage, /*contactUsPage,*/ topMenu } = useSiteNavigation(props);
const { isMobile, height, compactMenu } = useSiteHeader();
const { mobileMenu, desktopMenu, resetMenu } = useSiteTopMenu();
// const contactUs = computed(() => contactUsPage.value?.childPages?.find(x => x.displayName == (isMobile.value ? 'mobile' : 'desktop')));

const expandSearch = ref(false);
const _currentLanguage = useVModel(props, 'currentLanguage');
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/_index' as *;
@use 'sass:map';

.site-header {
    &__content {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        z-index: 1;
    }

    .v-btn {
        text-transform: none !important;

        &--slim {
            padding: 0 8px !important;
        }

        &--icon-green {
            background-color: var(--v-color-green-darken-1);
        }
    }

    .menu-btn {
        padding: 0;

        &__icon {
            transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);

            &--active {
                transform: rotate(180deg);
            }
        }
    }

    .site-logo {
        &__container {
            @media #{map.get($display-breakpoints, 'xl-and-up')} {
                margin-right: 100px;
            }
        }
    }
}
</style>
